import { useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min';
import type { ToolUiMetadata, ToolUiType } from '../types/events';
import { useDirection } from '../i18n/useDirection';

export interface ToolUiPanelProps {
  /** The tool UI to render an interactive body for. */
  toolUi: ToolUiMetadata;
  /**
   * Called when the user completes the interaction.
   * `result` is the payload sent back to the agent; `summary` is a short
   * human-readable confirmation shown in the chat afterwards.
   */
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}

interface FormData {
  first_name: string;
  last_name: string;
  last_name_prefix: string;
  mobile_phone: string;
  email: string;
}

interface CountryCodeOption {
  country: string;
  code: string;
  label: string;
}

interface TimeSlot {
  time: string;
  datetime: string;
  employee_id: number;
  available_seats: number;
}

interface ExistingAppointment {
  id: string;
  datetime: string;
  title?: string;
  transition_id?: string | number | null;
  transition_name?: string;
}

/**
 * Renders the interactive body for a tool UI (form / pickers) inside the
 * bottom sheet. Holds its own field state; calls `onSubmit` once when the
 * user completes the interaction.
 */
export function ToolUiPanel({ toolUi, onSubmit }: ToolUiPanelProps) {
  const { t, i18n } = useTranslation('widget');
  const { isRTL } = useDirection();
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  // Which edge text sits against is a layout question, so it keys off the club's setting. Which
  // order the characters come out in is a content question, answered by `dir="auto"` on the element.
  // `text-start` cannot serve both: under `dir="auto"` it resolves against the element's own
  // resolved direction, which left Latin placeholders on the opposite edge from their labels.
  const optionAlignClass = isRTL ? 'text-right' : 'text-left';

  // Guard against double submission (the sheet unmounts shortly after submit).
  const submittedRef = useRef(false);
  const submit = (result: Record<string, unknown>, summary?: string) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(result, summary);
  };

  switch (toolUi.ui_type) {
    case 'date_picker':
      return <DatePickerBody toolUi={toolUi} t={t} locale={locale} alignClass={optionAlignClass} onSubmit={submit} />;
    case 'location_picker':
      return <LocationPickerBody toolUi={toolUi} optionAlignClass={optionAlignClass} onSubmit={submit} />;
    case 'appointment_picker':
      return <AppointmentPickerBody toolUi={toolUi} t={t} locale={locale} optionAlignClass={optionAlignClass} onSubmit={submit} />;
    case 'user_form':
      return <UserFormBody toolUi={toolUi} t={t} i18n={i18n} locale={locale} alignClass={optionAlignClass} onSubmit={submit} />;
    case 'slot_selection':
      return <SlotSelectionBody toolUi={toolUi} t={t} locale={locale} isRTL={isRTL} optionAlignClass={optionAlignClass} onSubmit={submit} />;
    default:
      return null;
  }
}

// ============================================================================
// Date picker
// ============================================================================

function DatePickerBody({
  toolUi,
  t,
  locale,
  alignClass,
  onSubmit,
}: {
  toolUi: ToolUiMetadata;
  t: TFunction;
  locale: string;
  /** Which edge the label sits against; follows the layout, not the text. */
  alignClass: string;
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}) {
  const [selectedDate, setSelectedDate] = useState('');
  const { minDate, maxDate } = useMemo(() => {
    const config = (toolUi.ui_config || {}) as Record<string, unknown>;
    const normalize = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : undefined;
    return { minDate: normalize(config.min_date), maxDate: normalize(config.max_date) };
  }, [toolUi]);

  const handleSubmit = () => {
    if (!selectedDate) return;
    onSubmit({ date: selectedDate }, formatIsoDate(selectedDate, locale));
  };

  return (
    <div>
      <label
        htmlFor="cp-date-picker"
        className={`block text-xs font-medium mb-1 text-gray-700 ${alignClass}`}
        dir="auto"
      >
        {t('datePicker.selectDate')}
      </label>
      {/* Deliberately no `dir="auto"` here, unlike the free-text fields. A date value is all digits
          and separators — no strong character — so `auto` always resolves to ltr and would pin the
          native control's own layout (the dd/mm/yyyy fields and the calendar button) left-to-right
          in a mirrored sheet. Inheriting the layout's direction is what flips them. */}
      <input
        id="cp-date-picker"
        type="date"
        value={selectedDate}
        min={minDate}
        max={maxDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900"
      />
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selectedDate}
        className="mt-3 w-full rounded-lg bg-gray-900 px-3 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('datePicker.confirmDate')}
      </button>
    </div>
  );
}

// ============================================================================
// Location picker
// ============================================================================

function LocationPickerBody({
  toolUi,
  optionAlignClass,
  onSubmit,
}: {
  toolUi: ToolUiMetadata;
  optionAlignClass: string;
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}) {
  const locations = (toolUi.ui_config?.all_locations as Array<{ id?: string; name: string }>) || [];
  return (
    <div className="space-y-2">
      {locations.map((loc) => (
        <button
          key={loc.id ?? loc.name}
          type="button"
          onClick={() =>
            // Submit the authoritative location id when available; keep the
            // name for agent context and backward compatibility.
            onSubmit({ location_id: loc.id, location: loc.name }, `📍 ${loc.name}`)
          }
          className={`w-full ${optionAlignClass} px-3 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors text-sm font-medium text-gray-900`}
          dir="auto"
        >
          📍 {loc.name}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Appointment picker
// ============================================================================

function AppointmentPickerBody({
  toolUi,
  t,
  locale,
  optionAlignClass,
  onSubmit,
}: {
  toolUi: ToolUiMetadata;
  t: TFunction;
  locale: string;
  optionAlignClass: string;
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}) {
  const config = toolUi.ui_config as Record<string, unknown> | undefined;
  const appointments = (config?.appointments as ExistingAppointment[] | undefined) || [];
  const label = (config?.label as string | undefined) || t('appointmentPicker.chooseAppointment');

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-500 px-1">{label}</p>
      {appointments.map((appt) => {
        const display = appt.title || formatDateTime(appt.datetime, locale);
        return (
          <button
            key={appt.id}
            type="button"
            onClick={() =>
              onSubmit(
                {
                  appointment_id: appt.id,
                  datetime: appt.datetime,
                  transition_id: appt.transition_id,
                  transition_name: appt.transition_name,
                },
                display
              )
            }
            className={`w-full ${optionAlignClass} px-3 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors text-sm font-medium text-gray-900`}
            dir="auto"
          >
            {display}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Slot selection (date then time)
// ============================================================================

function SlotSelectionBody({
  toolUi,
  t,
  locale,
  isRTL,
  optionAlignClass,
  onSubmit,
}: {
  toolUi: ToolUiMetadata;
  t: TFunction;
  locale: string;
  isRTL: boolean;
  optionAlignClass: string;
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}) {
  const [selectedSlotDate, setSelectedSlotDate] = useState<string | null>(null);
  const config = toolUi.ui_config as Record<string, unknown> | undefined;
  const dates = (config?.dates ?? {}) as Record<string, TimeSlot[]>;
  const labels = (config?.labels ?? {}) as Record<string, string>;
  const pickDateLabel = labels.pick_date || t('slotSelection.pickDate');
  const pickTimeLabel = labels.pick_time || t('slotSelection.pickTime');
  const noTimesLabel = labels.no_times || t('slotSelection.noTimes');
  const sortedDates = Object.keys(dates).sort();
  const formatDay = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });

  const handleTimeSelect = (slot: TimeSlot) => {
    onSubmit(
      {
        datetime: slot.datetime,
        transition_id: config?.transition_id,
        transition_name: config?.transition_name,
      },
      formatDateTime(slot.datetime, locale)
    );
  };

  if (!selectedSlotDate) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-500 px-1">{pickDateLabel}</p>
        {sortedDates.map((date) => (
          <button
            key={date}
            type="button"
            onClick={() => setSelectedSlotDate(date)}
            className={`w-full ${optionAlignClass} px-3 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors text-sm font-medium text-gray-900`}
            dir="auto"
          >
            {formatDay(date)}
          </button>
        ))}
      </div>
    );
  }

  const slots = dates[selectedSlotDate] || [];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        <button
          type="button"
          onClick={() => setSelectedSlotDate(null)}
          className="text-sm text-gray-500 hover:text-gray-700"
          aria-label={pickDateLabel}
        >
          {isRTL ? '→' : '←'}
        </button>
        <p className="text-xs font-medium text-gray-500">
          {formatDay(selectedSlotDate)} &mdash; {pickTimeLabel}
        </p>
      </div>
      {slots.length === 0 && <p className="px-1 py-2 text-xs text-gray-500">{noTimesLabel}</p>}
      {slots.map((slot) => (
        <button
          key={slot.datetime}
          type="button"
          onClick={() => handleTimeSelect(slot)}
          className={`w-full ${optionAlignClass} px-3 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 transition-colors text-sm font-medium text-gray-900`}
          dir="auto"
        >
          {slot.time}
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// User form
// ============================================================================

function UserFormBody({
  toolUi,
  t,
  i18n,
  locale,
  alignClass,
  onSubmit,
}: {
  toolUi: ToolUiMetadata;
  t: TFunction;
  i18n: ReturnType<typeof useTranslation>['i18n'];
  locale: string;
  /** Which edge labels and fields sit against; follows the layout, not the text. */
  alignClass: string;
  onSubmit: (result: Record<string, unknown>, summary?: string) => void;
}) {
  const formConfig = toolUi.ui_config as Record<string, unknown> | undefined;
  const formSchema = formConfig?.form_schema as Record<string, unknown> | undefined;
  const formLanguage = getStringValue(formConfig?.language);
  const formT = useMemo(
    () => (formLanguage ? i18n.getFixedT(formLanguage, 'widget') : t),
    [formLanguage, i18n, t]
  );

  const countryCodeOptions = useMemo(() => buildCountryCodeOptions(), []);
  const preferredCountryCodes = useMemo(() => getPreferredCountryCodes(locale), [locale]);
  const defaultCountryCode = useMemo(() => {
    for (const country of preferredCountryCodes) {
      const match = countryCodeOptions.find((option) => option.country === country);
      if (match) return match.code;
    }
    return countryCodeOptions[0]?.code ?? '+1';
  }, [countryCodeOptions, preferredCountryCodes]);

  // Anything already known arrives from the server (CAIV-362). A visitor who typed their name
  // and number into the pre-chat gate must not be handed an empty form later, so the fields are
  // seeded once, when the card is first rendered.
  const prefilled = (formConfig?.prefilled as Record<string, string> | undefined) ?? {};
  // The phone is stored split: a country code beside a national number, recombined on submit.
  // A prefilled E.164 string therefore has to be taken apart, or the country code would be
  // counted twice and the submitted number would be wrong.
  const prefilledPhone = splitPrefilledPhone(prefilled.mobile_phone ?? '', countryCodeOptions);

  const [phoneCountryCode, setPhoneCountryCode] = useState(
    prefilledPhone.countryCode ?? defaultCountryCode
  );
  const [formData, setFormData] = useState<FormData>({
    first_name: prefilled.first_name ?? '',
    last_name: prefilled.last_name ?? '',
    last_name_prefix: prefilled.last_name_prefix ?? '',
    mobile_phone: prefilledPhone.national,
    email: prefilled.email ?? '',
  });

  useEffect(() => {
    if (!phoneCountryCode || !countryCodeOptions.some((option) => option.code === phoneCountryCode)) {
      setPhoneCountryCode(defaultCountryCode);
    }
  }, [countryCodeOptions, defaultCountryCode, phoneCountryCode]);

  const preferredCountrySet = useMemo(() => new Set(preferredCountryCodes), [preferredCountryCodes]);
  const preferredOptions = useMemo(
    () => countryCodeOptions.filter((option) => preferredCountrySet.has(option.country)),
    [countryCodeOptions, preferredCountrySet]
  );
  const remainingOptions = useMemo(
    () => countryCodeOptions.filter((option) => !preferredCountrySet.has(option.country)),
    [countryCodeOptions, preferredCountrySet]
  );

  const formProperties =
    formSchema?.properties && typeof formSchema.properties === 'object' && !Array.isArray(formSchema.properties)
      ? (formSchema.properties as Record<string, unknown>)
      : {};
  const getFormProperty = (field: string) =>
    formProperties[field] && typeof formProperties[field] === 'object' && !Array.isArray(formProperties[field])
      ? (formProperties[field] as Record<string, unknown>)
      : undefined;
  const getFormLabel = (field: string, key: string) =>
    getStringValue(getFormProperty(field)?.title) || formT(key);
  const getFormPlaceholder = (field: string, key: string, defaultValue?: string) =>
    getStringValue(getFormProperty(field)?.description) ||
    formT(key, defaultValue ? { defaultValue } : undefined);

  const formConsent = getStringValue(formConfig?.consent);

  const formRequired = new Set<string>(
    ((formSchema?.required as string[] | undefined) || ['first_name', 'last_name', 'mobile_phone', 'email']).filter(
      Boolean
    )
  );
  const formFields =
    (formConfig?.fields as string[] | undefined) ||
    ['first_name', 'last_name', 'last_name_prefix', 'mobile_phone', 'email'];
  const showField = (field: string) => formFields.includes(field);
  const isRequiredField = (field: string) => formRequired.has(field);

  const normalizedPhone = buildE164Phone(phoneCountryCode, formData.mobile_phone);
  const isPhoneValid = normalizedPhone.length > 0 && E164_PHONE_PATTERN.test(normalizedPhone);

  const isFormValid = Array.from(formRequired).every((field) => {
    if (field === 'mobile_phone' && showField('mobile_phone')) return isPhoneValid;
    const value = formData[field as keyof FormData];
    return typeof value === 'string' && value.trim().length > 0;
  });

  const handleSubmit = () => {
    if (!isFormValid) return;
    const result: Record<string, unknown> = {};
    const allFormKeys: Array<keyof FormData> = [
      'first_name',
      'last_name',
      'last_name_prefix',
      'mobile_phone',
      'email',
    ];
    allFormKeys.forEach((key) => {
      if (!showField(key)) return;
      const value = key === 'mobile_phone' ? normalizedPhone : formData[key]?.trim();
      if (value) result[key] = value;
    });
    onSubmit(result, formT('form.submitted'));
  };

  const inputClass = `w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 ${alignClass}`;
  const labelClass = `block text-xs font-medium mb-1 text-gray-700 ${alignClass}`;

  return (
    <div className="text-gray-900">
      <div className="space-y-3">
        {(showField('first_name') || showField('last_name')) && (
          <div className="grid grid-cols-2 gap-2">
      {showField('first_name') && (
              <div>
                <label className={labelClass} dir="auto">
                  {getFormLabel('first_name', 'form.firstName')} {isRequiredField('first_name') ? '*' : ''}
                </label>
                <input
                  dir="auto"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                  className={inputClass}
                  placeholder={getFormPlaceholder('first_name', 'form.placeholders.firstName')}
                />
              </div>
            )}
            {showField('last_name') && (
              <div>
                <label className={labelClass} dir="auto">
                  {getFormLabel('last_name', 'form.lastName')} {isRequiredField('last_name') ? '*' : ''}
                </label>
                <input
                  dir="auto"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                  className={inputClass}
                  placeholder={getFormPlaceholder('last_name', 'form.placeholders.lastName')}
                />
              </div>
            )}
          </div>
        )}
        {showField('last_name_prefix') && (
          <div>
            <label className={labelClass} dir="auto">
              {getFormLabel('last_name_prefix', 'form.lastNamePrefix')} {isRequiredField('last_name_prefix') ? '*' : ''}
            </label>
            <input
              dir="auto"
              type="text"
              value={formData.last_name_prefix}
              onChange={(e) => setFormData((prev) => ({ ...prev, last_name_prefix: e.target.value }))}
              className={inputClass}
              placeholder={getFormPlaceholder('last_name_prefix', 'form.placeholders.lastNamePrefix')}
            />
          </div>
        )}
        {showField('mobile_phone') && (
          <div>
            <label className={labelClass} dir="auto">
              {getFormLabel('mobile_phone', 'form.phone')} {isRequiredField('mobile_phone') ? '*' : ''}
            </label>
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <select
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-2 py-2.5 text-sm bg-white text-gray-900"
                aria-label={formT('form.countryCode', { defaultValue: 'Country code' })}
              >
                {preferredOptions.length > 0 && (
                  <optgroup label={formT('form.countryCodeSuggested', { defaultValue: 'Suggested' })}>
                    {preferredOptions.map((option) => (
                      <option key={option.country} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label={formT('form.countryCodeAll', { defaultValue: 'All countries' })}>
                  {remainingOptions.map((option) => (
                    <option key={option.country} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              <input
                dir="auto"
                type="tel"
                value={formData.mobile_phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, mobile_phone: e.target.value }))}
                className={inputClass}
                placeholder={getFormPlaceholder('mobile_phone', 'form.placeholders.phoneLocal', '612345678')}
              />
            </div>
            {formData.mobile_phone.trim().length > 0 && !isPhoneValid && (
              <p className={`mt-1 text-xs text-red-600 ${alignClass}`} dir="auto">
                {formT('form.phoneInvalid', { defaultValue: 'Enter a valid phone number with country code.' })}
              </p>
            )}
          </div>
        )}
        {showField('email') && (
          <div>
            <label className={labelClass} dir="auto">
              {getFormLabel('email', 'form.email')} {isRequiredField('email') ? '*' : ''}
            </label>
            <input
              dir="auto"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              className={inputClass}
              placeholder={getFormPlaceholder('email', 'form.placeholders.email')}
            />
          </div>
        )}
      </div>
      {formConsent && (
        <p className="mt-3 text-xs leading-snug text-gray-500" dir="auto">
          {formConsent}
        </p>
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isFormValid}
        className="mt-4 w-full rounded-lg bg-gray-900 px-3 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {formT('form.submit')}
      </button>
    </div>
  );
}

// ============================================================================
// Shared helpers (title / icon / formatting / phone)
// ============================================================================

/** Emoji icon shown on the inline trigger card for each tool UI type. */
/**
 * Split a prefilled E.164 number into the country code the picker shows and the national part
 * the text field holds.
 *
 * Longest match wins: several country codes share a prefix, and taking the first hit would file
 * a +1-something number under +1 and leave a digit stranded in the national part.
 */
export function splitPrefilledPhone(
  value: string,
  options: Array<{ code: string }>
): { countryCode: string | null; national: string } {
  const trimmed = (value || '').trim();
  if (!trimmed.startsWith('+')) return { countryCode: null, national: trimmed };
  const match = options
    .map((option) => option.code)
    .filter((code) => trimmed.startsWith(code))
    .sort((a, b) => b.length - a.length)[0];
  if (!match) return { countryCode: null, national: trimmed };
  return { countryCode: match, national: trimmed.slice(match.length) };
}

export function getToolUiIcon(uiType: ToolUiType): string {
  switch (uiType) {
    case 'user_form':
      return '📝';
    case 'location_picker':
      return '📍';
    case 'date_picker':
    case 'slot_selection':
      return '📅';
    case 'appointment_picker':
      return '🗓️';
    default:
      return '💬';
  }
}

/** Localised sheet / card title for a tool UI type. */
export function getToolUiTitle(t: TFunction, uiType: ToolUiType): string {
  switch (uiType) {
    case 'user_form':
      return t('sheet.formTitle');
    case 'location_picker':
      return t('sheet.locationTitle');
    case 'date_picker':
      return t('datePicker.selectDate');
    case 'appointment_picker':
      return t('appointmentPicker.chooseAppointment');
    case 'slot_selection':
      return t('slotSelection.pickDate');
    default:
      return '';
  }
}

function formatDateTime(value: string, locale: string): string {
  const safe = value.includes('T') ? value : value.replace(' ', 'T');
  const dt = new Date(safe);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatIsoDate(value: string, locale: string): string {
  const dt = new Date(value + 'T00:00:00');
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

const E164_PHONE_PATTERN = /^\+[1-9]\d{6,13}$/;

const LANGUAGE_TOP_COUNTRIES: Record<string, string[]> = {
  en: ['US', 'GB', 'CA', 'AU', 'IE'],
  nl: ['NL', 'BE', 'DE', 'GB', 'FR'],
  de: ['DE', 'AT', 'CH', 'NL', 'GB'],
  fr: ['FR', 'BE', 'CH', 'CA', 'LU'],
  es: ['ES', 'MX', 'AR', 'CO', 'CL'],
  it: ['IT', 'CH', 'SM', 'VA', 'DE'],
  pt: ['PT', 'BR', 'AO', 'MZ', 'CV'],
  ar: ['SA', 'AE', 'EG', 'MA', 'QA'],
};

function getPreferredCountryCodes(language: string): string[] {
  const base = language.toLowerCase().split('-')[0];
  return LANGUAGE_TOP_COUNTRIES[base] || LANGUAGE_TOP_COUNTRIES.en;
}

function buildCountryCodeOptions(): CountryCodeOption[] {
  return getCountries()
    .map((country) => {
      const callingCode = getCountryCallingCode(country);
      const flag = countryCodeToFlagEmoji(country);
      return { country, code: `+${callingCode}`, label: `${flag} +${callingCode}` };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function buildE164Phone(countryCode: string, localNumber: string): string {
  const cleanCountry = (countryCode || '').trim();
  const cleanLocal = (localNumber || '').replace(/\D+/g, '');
  if (!cleanCountry || !cleanLocal) return '';
  return `${cleanCountry}${cleanLocal}`;
}

function countryCodeToFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '';
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('');
}

export default ToolUiPanel;
